/**
 * Accounts payable Redux (HPRMS Phase E) — vendor bills + vendor payments.
 */

const initialState = {
  vendorBills: [],
  vendorPayments: [],
  billsLoaded: false,
  paymentsLoaded: false,
};

const CLIENT_UPSERT_TTL_MS = 120000;

const entityKey = (row) => {
  if (!row || typeof row !== "object") return "";
  for (const v of [row.POID, row.poid, row.id, row.billId, row.paymentId]) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s && s !== "null" && s !== "undefined" && s !== "0") return s;
  }
  return "";
};

const hasLines = (row) =>
  Array.isArray(row?.lineItems) && row.lineItems.length > 0;

const mergePreserveDetail = (existing, incoming) => {
  if (!incoming) return existing || null;
  if (!existing) return incoming;
  const keepLines =
    !hasLines(incoming) && hasLines(existing) ? existing.lineItems : incoming.lineItems;
  const keepFetched = Boolean(existing.__detailFetched || incoming.__detailFetched);
  const keepAllocs =
    (!incoming.allocations || incoming.allocations.length === 0) &&
    Array.isArray(existing.allocations) &&
    existing.allocations.length > 0
      ? existing.allocations
      : incoming.allocations;
  return {
    ...existing,
    ...incoming,
    lineItems: keepLines ?? existing.lineItems ?? incoming.lineItems ?? [],
    ...(keepAllocs ? { allocations: keepAllocs } : {}),
    ...(keepFetched ? { __detailFetched: true } : {}),
  };
};

const mergeRemoteWithLocal = (localList, remoteRaw) => {
  const remote = Array.isArray(remoteRaw) ? remoteRaw : remoteRaw ? [remoteRaw] : [];
  const localByKey = new Map();
  (localList || []).forEach((row) => {
    const key = entityKey(row);
    if (key) localByKey.set(key, row);
  });
  const now = Date.now();
  const remoteKeys = new Set();
  const merged = remote.map((r) => {
    const key = entityKey(r);
    if (key) remoteKeys.add(key);
    const local = key ? localByKey.get(key) : null;
    if (!local) return { ...r, POID: key || r.POID, id: key || r.id };
    const localIsFresh =
      typeof local._clientUpsertAt === "number" &&
      now - local._clientUpsertAt < CLIENT_UPSERT_TTL_MS;
    const next = mergePreserveDetail(local, r);
    return {
      ...next,
      status: localIsFresh && local.status ? local.status : next.status || local.status,
      paidAmount:
        localIsFresh && local.paidAmount != null ? local.paidAmount : next.paidAmount,
      dueAmount:
        localIsFresh && local.dueAmount != null ? local.dueAmount : next.dueAmount,
      _clientUpsertAt: local._clientUpsertAt || r._clientUpsertAt,
    };
  });
  const extras = [];
  localByKey.forEach((row, key) => {
    if (remoteKeys.has(key)) return;
    const at = row._clientUpsertAt;
    if (typeof at === "number" && now - at < CLIENT_UPSERT_TTL_MS) extras.push(row);
  });
  return extras.length ? [...extras, ...merged] : merged;
};

const upsertOne = (list, row) => {
  if (!row) return list || [];
  const key = entityKey(row);
  if (!key) return [row, ...(list || [])];
  const stamped = {
    ...row,
    POID: key,
    id: key,
    _clientUpsertAt: Date.now(),
  };
  const next = [...(list || [])];
  const idx = next.findIndex((x) => entityKey(x) === key);
  if (idx >= 0) next[idx] = mergePreserveDetail(next[idx], stamped);
  else next.unshift(stamped);
  return next;
};

const accountsPayableReducer = (state = initialState, action) => {
  switch (action.type) {
    case "SET_VENDOR_BILLS":
      return {
        ...state,
        vendorBills: mergeRemoteWithLocal(state.vendorBills, action.payload),
        billsLoaded: true,
      };
    case "UPSERT_VENDOR_BILL":
      return {
        ...state,
        vendorBills: upsertOne(state.vendorBills, action.payload),
        billsLoaded: true,
      };
    case "PATCH_VENDOR_BILL": {
      const key = String(action.id ?? "");
      const patch = action.patch || {};
      if (!key || key === "null") return state;
      return {
        ...state,
        vendorBills: (state.vendorBills || []).map((b) =>
          entityKey(b) === key
            ? { ...b, ...patch, POID: key, id: key, _clientUpsertAt: Date.now() }
            : b
        ),
      };
    }
    case "SET_VENDOR_PAYMENTS":
      return {
        ...state,
        vendorPayments: mergeRemoteWithLocal(state.vendorPayments, action.payload),
        paymentsLoaded: true,
      };
    case "UPSERT_VENDOR_PAYMENT":
      return {
        ...state,
        vendorPayments: upsertOne(state.vendorPayments, action.payload),
        paymentsLoaded: true,
      };
    case "CLEAR_ACCOUNTS_PAYABLE_DATA":
      return { ...initialState };
    default:
      return state;
  }
};

export default accountsPayableReducer;
export { entityKey as apEntityKey, mergePreserveDetail as mergeApPreserveDetail };
